import React from 'react'
import './TabProfile.scss'
import { Form, Input, Button, Divider, notification } from 'antd'
import { useTranslation } from 'react-i18next'

import { updateUserPortalPromise } from '../../promises'

const TabProfile = ({ form, user, onChangeTab, refreshUser }) => {
	const { t } = useTranslation()
	const { getFieldDecorator, validateFields, getFieldValue } = form

	const handleSubmit = (e) => {
		e.preventDefault()
		validateFields((err, values) => {
			if (err) {
				return
			}

			const { nombre, email, passwordCurrent, passwordNew, passwordNewConfirm } = values

			updateUserPortalPromise(
				user?.pais,
				user?.rut,
				nombre,
				email,
				passwordCurrent,
				passwordNew,
				passwordNewConfirm,
			).then((resp) => {
				const response = resp.data

				if (response && (response.status === 201 || response.status === 202 || (response.data && (response.data.code === '202-00' || response.data.code === '202')))) {
					notification.success({
						message: t('messages.aml.successfulOperation'),
						description: t('messages.aml.success.passwordSuccesfullyUpdated')
					})
					if (onChangeTab) {
						refreshUser({...user, nombre, email })
						onChangeTab('home')
					}
				} else {
					const code = (response.data && response.data.data && response.data.data.code) || (response.data && response.data.code)
					const errorMessages = {
						"400-10": 'messages.aml.errors.passwordCannotBeInLogin',
						"400-20": 'messages.aml.errors.passwordMustHaveBetween8and30Chars',
						"400-30": 'messages.aml.errors.passwordMustBeAlphanumeric',
						"400-40": 'messages.aml.errors.passwordMustHaveLowerCaseLetter',
						"400-50": 'messages.aml.errors.passwordMustHaveUpperCaseLetter',
						"400-60": 'messages.aml.errors.youCannotUsePreviousPasswords',
						"400-70": 'messages.aml.errors.invalid.current.pwd',
						"400-80": 'messages.aml.errors.newPwdIdenticalToCurrent',
						"400-90": 'messages.aml.errors.emailNotFoundInClientRecords',
						"500": 'messages.aml.errors.unknownError'
					}
					const validationMessage = errorMessages[code] || 'messages.aml.errors.unknownError'
					notification.error({ message: t(validationMessage) })
				}
			}).catch(() => {
				notification.error({ message: t('messages.aml.errors.unknownError') })
			})
		})
	}

	const passwordPolicyValidators = [
		{
			validator: (_, value) => {
				if (value && (value.length < 8 || value.length > 30)) {
					return Promise.reject(new Error(t('messages.aml.errors.pwd.long.validation')))
				}
				return Promise.resolve()
			}
		},
		{
			validator: (_, value) => {
				const allowedChars = /^[a-zA-Z0-9&*?@#!$%\-_]+$/
				const hasUppercase = /[A-Z]/
				const hasLowercase = /[a-z]/
				const hasLetter = /[a-zA-Z]/
				const hasDigit = /[0-9]/

				if (!value) {
					return Promise.resolve()
				}

				if (!hasUppercase.test(value)) {
					return Promise.reject(new Error(t('messages.aml.validation.passwordMustHaveUpperCaseLetter')))
				}

				if (!hasLowercase.test(value)) {
					return Promise.reject(new Error(t('messages.aml.validation.passwordMustHaveLowerCaseLetter')))
				}

				if (!hasDigit.test(value) || !hasLetter.test(value)) {
					return Promise.reject(new Error(t('messages.aml.validation.password.must.be.alphanum')))
				}

				if (!allowedChars.test(value)) {
					return Promise.reject(new Error(t('messages.aml.errors.pwd.special.characters')))
				}
				return Promise.resolve()
			}
		}
	]

	return (
		<div className="tab-profile">
			<h2>{t('portal.profile.title')}</h2>

			<Form layout="vertical" onSubmit={handleSubmit} autoComplete="off">
				<Form.Item label={t('messages.aml.name')}>
					{getFieldDecorator('nombre', {
						initialValue: user?.nombre,
						rules: [
							{
								required: true,
								message: t('messages.aml.missingRequiredField')
							},
							{
								max: 200,
								message: t('messages.aml.errors.maxLength200')
							}
						]
					})(
						<Input />
					)}
				</Form.Item>

				<Form.Item label={t('messages.aml.email')}>
					{getFieldDecorator('email', {
						initialValue: user?.email,
						rules: [
							{
								required: true,
								message: t('messages.aml.missingRequiredField')
							},
							{
								type: 'email',
								message: t('messages.aml.emailNotValid')
							},
							{
								max: 200,
								message: t('messages.aml.errors.maxLength200')
							}
						]
					})(
						<Input />
					)}
				</Form.Item>

				<Divider />

				<h3>{t('portal.profile.changePassword.optional')}</h3>
				<p className="tab-profile-password-help">
					{t('portal.profile.passwordPolicy')}
				</p>

				<Form.Item label={t('messages.aml.currentPassword')}>
					{getFieldDecorator('passwordCurrent', {
						rules: [
							{
								validator: (_, value) => {
									const newPassword = getFieldValue('passwordNew')
									const confirmPassword = getFieldValue('passwordNewConfirm')
									if ((newPassword || confirmPassword) && !value) {
										return Promise.reject(new Error(t('messages.aml.missingRequiredField')))
									}
									return Promise.resolve()
								}
							}
						]
					})(
						<Input.Password autoComplete="new-password" />
					)}
				</Form.Item>

				<Form.Item label={t('messages.aml.newPassword')}>
					{getFieldDecorator('passwordNew', {
						rules: [
							{
								validator: (_, value) => {
									const currentPassword = getFieldValue('passwordCurrent')
									const confirmPassword = getFieldValue('passwordNewConfirm')
									if ((currentPassword || confirmPassword) && !value) {
										return Promise.reject(new Error(t('messages.aml.missingRequiredField')))
									}
									return Promise.resolve()
								}
							},
							...passwordPolicyValidators
						]
					})(
						<Input.Password autoComplete="off" />
					)}
				</Form.Item>

				<Form.Item label={t('messages.aml.newPasswordConfirm')}>
					{getFieldDecorator('passwordNewConfirm', {
						rules: [
							{
								validator: (_, value) => {
									const currentPassword = getFieldValue('passwordCurrent')
									const newPassword = getFieldValue('passwordNew')
									if ((currentPassword || newPassword) && !value) {
										return Promise.reject(new Error(t('messages.aml.missingRequiredField')))
									}
									return Promise.resolve()
								}
							},
							...passwordPolicyValidators,
							{
								validator: (_, value) => {
									const newPassword = getFieldValue('passwordNew')
									if (value && newPassword && value !== newPassword) {
										return Promise.reject(new Error(t('messages.aml.errors.pwd.same.validation')))
									}
									return Promise.resolve()
								}
							}
						]
					})(
					<Input.Password autoComplete="off" />
					)}
				</Form.Item>

				<Form.Item>
					<Button type="primary" htmlType="submit">
						{t('portal.profile.saveChanges')}
					</Button>
				</Form.Item>
			</Form>
		</div>
	)
}

const WrappedTabProfile = Form.create({ name: 'portal_profile_form' })(TabProfile)

export default WrappedTabProfile

